import { useCallback, useMemo, useState } from 'react';
import { ROCK_STATUSES, BOARD_COLUMNS } from '../types';
import { formatDate } from '@/lib/utils';
import { RockStatusBadge } from '../components/RockStatusBadge';
import { ChevronRight } from 'lucide-react';
import type { Rock, Team } from '../types';
import type { OwnerOption } from '../api';
import { createElement } from 'react';
import type { DataTableColumn } from '@/shared/components/data-table';
import { OwnerAvatar } from '@/shared/components/OwnerAvatar';

export interface RockRenderContext {
  ownerMap: Map<string, string>;
  teamMap: Map<string, string>;
}

export type RockColumnDef = DataTableColumn<Rock, RockRenderContext> & {
  /** Used by the Columns dropdown to initialise visibility. */
  defaultVisible?: boolean;
};

const ALL_COLUMN_KEYS = ['title', 'owner', 'status', 'type', 'quarter', 'team', 'boardColumn', 'dueDate', 'actions'];
const HIDDEN_BY_DEFAULT = new Set(['team', 'boardColumn']);

const VISIBILITY_KEY = 'atek-rocks-column-visibility';
const FILTERS_KEY = 'atek-rocks-column-filters';

function getDefaultVisibility(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const key of ALL_COLUMN_KEYS) {
    defaults[key] = !HIDDEN_BY_DEFAULT.has(key);
  }
  return defaults;
}

function loadVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_KEY);
    if (stored) return { ...getDefaultVisibility(), ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return getDefaultVisibility();
}

function loadColumnFilters(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}');
  } catch { return {}; }
}

function buildColumns(
  ownerOptions: { label: string; value: string }[],
  quarterOptions: { label: string; value: string }[],
  teamOptions: { label: string; value: string }[],
): RockColumnDef[] {
  return [
    {
      key: 'title',
      label: 'Title',
      sortKey: 'title',
      filterType: 'text',
      isPrimarySearch: true,
      searchPlaceholder: 'Search rock...',
      getValue: (r) => r.title,
      render: (r) => createElement('span', { className: 'font-medium' }, r.title),
    },
    {
      key: 'owner',
      label: 'Owner',
      sortKey: 'owner',
      filterType: 'select',
      filterOptions: ownerOptions,
      filterAllLabel: 'All Owners',
      getValue: (r, ctx) => ctx.ownerMap.get(r.ownerId) ?? r.ownerId.slice(-6),
      render: (r, ctx) => {
        const name = ctx.ownerMap.get(r.ownerId) ?? r.ownerId.slice(-6);
        return createElement(
          'div',
          { className: 'flex items-center gap-2' },
          createElement(OwnerAvatar, { ownerId: r.ownerId, name }),
          createElement('span', { className: 'text-sm' }, name),
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortKey: 'status',
      filterType: 'select',
      filterOptions: ROCK_STATUSES.map((s) => ({ label: s.label, value: s.value })),
      filterAllLabel: 'All Status',
      getValue: (r) => r.status,
      render: (r) => createElement(RockStatusBadge, { status: r.status }),
    },
    {
      key: 'type',
      label: 'Type',
      sortKey: 'type',
      filterType: 'select',
      filterOptions: [
        { label: 'Company', value: 'company' },
        { label: 'Individual', value: 'individual' },
      ],
      filterAllLabel: 'All Types',
      getValue: (r) => r.type,
      render: (r) => createElement('span', { className: 'capitalize text-sm' }, r.type),
    },
    {
      key: 'quarter',
      label: 'Quarter',
      sortKey: 'quarter',
      filterType: 'select',
      filterOptions: quarterOptions,
      filterAllLabel: 'All Quarters',
      getValue: (r) => r.quarter,
    },
    {
      key: 'team',
      label: 'Team',
      sortKey: 'team',
      filterType: 'select',
      filterOptions: teamOptions,
      filterAllLabel: 'All Teams',
      getValue: (r, ctx) => ctx.teamMap.get(r.teamId) ?? '—',
      defaultVisible: false,
    },
    {
      key: 'boardColumn',
      label: 'Board Column',
      sortKey: 'boardColumn',
      filterType: 'select',
      filterOptions: BOARD_COLUMNS.map((c) => ({ label: c.label, value: c.value })),
      filterAllLabel: 'All Columns',
      getValue: (r) => r.boardColumn,
      render: (r) => {
        const col = BOARD_COLUMNS.find((c) => c.value === r.boardColumn);
        return createElement('span', { className: 'text-sm' }, col?.label ?? r.boardColumn);
      },
      defaultVisible: false,
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      sortKey: 'dueDate',
      filterType: 'text',
      getValue: (r) => r.dueDate ?? '',
      render: (r) =>
        r.dueDate
          ? createElement('span', { className: 'text-sm' }, formatDate(r.dueDate))
          : createElement('span', { className: 'text-muted-foreground' }, '—'),
    },
    {
      key: 'actions',
      label: '',
      noFilter: true,
      getValue: () => '',
      render: () => createElement(ChevronRight, { className: 'h-4 w-4 text-gray-400' }),
    },
  ];
}

function escapeCSV(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function exportRocksCSV(items: Rock[], columns: RockColumnDef[], ctx: RockRenderContext) {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => escapeCSV(c.label)).join(',');
  const rows = items.map((item) =>
    exportCols.map((c) => escapeCSV(c.getValue(item, ctx))).join(','),
  );
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `rocks_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function copyRocksToClipboard(items: Rock[], columns: RockColumnDef[], ctx: RockRenderContext) {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => c.label).join('\t');
  const rows = items.map((item) =>
    exportCols.map((c) => String(c.getValue(item, ctx) ?? '')).join('\t'),
  );
  return navigator.clipboard.writeText([header, ...rows].join('\n'));
}

export interface UseRockListTableResult {
  sortKey: string;
  sortDirection: 1 | -1;
  columnFilters: Record<string, string>;
  columnVisibility: Record<string, boolean>;
  columns: RockColumnDef[];
  visibleColumns: RockColumnDef[];
  ctx: RockRenderContext;
  sortedRocks: Rock[];
  totalCount: number;
  isVisible: (key: string) => boolean;
  toggleColumn: (key: string) => void;
  setColumnFilter: (key: string, value: string) => void;
  handleSort: (key: string) => void;
}

export function useRockListTable(
  rocks: Rock[],
  owners: OwnerOption[],
  teams: Team[],
): UseRockListTableResult {
  const [sortKey, setSortKey] = useState<string>('title');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(loadColumnFilters);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(loadVisibility);

  const ownerMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of owners) m.set(o.id, o.name);
    return m;
  }, [owners]);

  const teamMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of teams) m.set(t.id, t.name);
    return m;
  }, [teams]);

  const ctx: RockRenderContext = useMemo(() => ({ ownerMap, teamMap }), [ownerMap, teamMap]);

  const ownerOptions = useMemo(() => {
    const seen = new Set<string>();
    return rocks.reduce<{ label: string; value: string }[]>((acc, r) => {
      const name = ownerMap.get(r.ownerId) ?? r.ownerId.slice(-6);
      if (!seen.has(name)) {
        seen.add(name);
        acc.push({ label: name, value: name });
      }
      return acc;
    }, []).sort((a, b) => a.label.localeCompare(b.label));
  }, [rocks, ownerMap]);

  const quarterOptions = useMemo(() => {
    const quarters = [...new Set(rocks.map((r) => r.quarter))].sort().reverse();
    return quarters.map((q) => ({ label: q, value: q }));
  }, [rocks]);

  const teamOptions = useMemo(() => {
    const seen = new Set<string>();
    return rocks.reduce<{ label: string; value: string }[]>((acc, r) => {
      const name = teamMap.get(r.teamId) ?? r.teamId;
      if (!seen.has(name)) {
        seen.add(name);
        acc.push({ label: name, value: name });
      }
      return acc;
    }, []).sort((a, b) => a.label.localeCompare(b.label));
  }, [rocks, teamMap]);

  const columns = useMemo(
    () => buildColumns(ownerOptions, quarterOptions, teamOptions),
    [ownerOptions, quarterOptions, teamOptions],
  );

  const isVisible = useCallback(
    (key: string) => columnVisibility[key] !== false,
    [columnVisibility],
  );

  const visibleColumns = useMemo(
    () => columns.filter((col) => columnVisibility[col.key] !== false),
    [columns, columnVisibility],
  );

  const toggleColumn = useCallback((key: string) => {
    setColumnVisibility((prev) => {
      const updated = { ...prev, [key]: !prev[key] };
      localStorage.setItem(VISIBILITY_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const setColumnFilter = useCallback((key: string, value: string) => {
    setColumnFilters((prev) => {
      const next = { ...prev, [key]: value };
      localStorage.setItem(FILTERS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const filteredRocks = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
    if (activeFilters.length === 0) return rocks;

    return rocks.filter((rock) =>
      activeFilters.every(([colKey, filterValue]) => {
        const col = columns.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;

        const rawValue = col.getValue(rock, ctx);

        if (col.filterType === 'select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return selected.length === 0 || selected.includes(String(rawValue || ''));
          } catch {
            return true;
          }
        }

        return String(rawValue ?? '').toLowerCase().includes(filterValue.toLowerCase());
      }),
    );
  }, [rocks, columnFilters, columns, ctx]);

  const sortedRocks = useMemo(() => {
    const col = columns.find((c) => c.sortKey === sortKey);
    if (!col) return filteredRocks;

    return [...filteredRocks].sort((a, b) => {
      const aVal = col.getValue(a, ctx);
      const bVal = col.getValue(b, ctx);
      const cmp = aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
      return cmp * sortDirection;
    });
  }, [filteredRocks, sortKey, sortDirection, columns, ctx]);

  const handleSort = useCallback(
    (key: string) => {
      if (sortKey === key) {
        setSortDirection((prev) => (prev === 1 ? -1 : 1));
      } else {
        setSortKey(key);
        setSortDirection(1);
      }
    },
    [sortKey],
  );

  return {
    sortKey,
    sortDirection,
    columnFilters,
    columnVisibility,
    columns,
    visibleColumns,
    ctx,
    sortedRocks,
    totalCount: rocks.length,
    isVisible,
    toggleColumn,
    setColumnFilter,
    handleSort,
  };
}
